import { useState, useEffect } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Edit, Trash2, Save, X, ArrowLeft } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminProjectsPage = () => {
  const { getAuthHeader } = useAdmin();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [formData, setFormData] = useState({
    title: '', category: '', description: '', challenge: '', solution: '',
    results: '', image: '', tags: '', note: 'Sample project for demonstration', order: 0
  });

  useEffect(() => { fetchProjects(); }, []);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/projects`);
      setProjects(response.data);
    } catch (error) {
      toast.error('Failed to load projects');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setEditingId(project.id);
    setFormData({
      title: project.title, category: project.category, description: project.description,
      challenge: project.challenge, solution: project.solution,
      results: project.results.join('\n'), image: project.image,
      tags: project.tags.join(', '), note: project.note, order: project.order
    });
  };

  const handleAdd = () => {
    setIsAdding(true);
    setFormData({ title: '', category: '', description: '', challenge: '', solution: '',
      results: '', image: '', tags: '', note: 'Sample project for demonstration', order: projects.length });
  };

  const handleCancel = () => {
    setEditingId(null); setIsAdding(false);
    setFormData({ title: '', category: '', description: '', challenge: '', solution: '',
      results: '', image: '', tags: '', note: '', order: 0 });
  };

  const handleSave = async () => {
    try {
      const data = {
        title: formData.title, category: formData.category, description: formData.description,
        challenge: formData.challenge, solution: formData.solution,
        results: formData.results.split('\n').filter(Boolean),
        image: formData.image, tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        note: formData.note, order: formData.order
      };

      if (isAdding) {
        await axios.post(`${API}/projects`, data, { headers: getAuthHeader() });
        toast.success('Project added successfully');
      } else {
        await axios.put(`${API}/projects/${editingId}`, data, { headers: getAuthHeader() });
        toast.success('Project updated successfully');
      }
      fetchProjects(); handleCancel();
    } catch (error) {
      toast.error('Failed to save project');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      await axios.delete(`${API}/projects/${id}`, { headers: getAuthHeader() });
      toast.success('Project deleted');
      fetchProjects();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin/dashboard"><Button variant="outline" size="icon" className="rounded-full"><ArrowLeft size={18} /></Button></Link>
              <div><h1 className="font-heading text-3xl font-bold">Manage Projects</h1><p className="text-slate-600">Add, edit, or remove portfolio projects</p></div>
            </div>
            {!isAdding && !editingId && <Button onClick={handleAdd} className="bg-[#10B981] hover:bg-[#059669] rounded-full"><Plus size={18} className="mr-2" />Add Project</Button>}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-12">
        {(isAdding || editingId) && (
          <div className="bg-white rounded-2xl p-8 border mb-8">
            <h2 className="font-heading text-2xl font-bold mb-6">{isAdding ? 'Add New Project' : 'Edit Project'}</h2>
            <div className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div><Label>Title</Label><Input value={formData.title} onChange={(e) => setFormData({...formData, title: e.target.value})} /></div>
                <div><Label>Category</Label><Input value={formData.category} onChange={(e) => setFormData({...formData, category: e.target.value})} placeholder="e.g., E-commerce" /></div>
              </div>
              <div><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} /></div>
              <div><Label>Challenge</Label><Textarea value={formData.challenge} onChange={(e) => setFormData({...formData, challenge: e.target.value})} rows={3} /></div>
              <div><Label>Solution</Label><Textarea value={formData.solution} onChange={(e) => setFormData({...formData, solution: e.target.value})} rows={3} /></div>
              <div><Label>Results (one per line)</Label><Textarea value={formData.results} onChange={(e) => setFormData({...formData, results: e.target.value})} rows={4} /></div>
              <div><Label>Image URL</Label><Input value={formData.image} onChange={(e) => setFormData({...formData, image: e.target.value})} /></div>
              <div><Label>Tags (comma-separated)</Label><Input value={formData.tags} onChange={(e) => setFormData({...formData, tags: e.target.value})} placeholder="Shopify, E-commerce, SEO" /></div>
              <div className="flex gap-4">
                <Button onClick={handleSave} className="bg-[#10B981] hover:bg-[#059669] rounded-full"><Save size={18} className="mr-2" />Save</Button>
                <Button onClick={handleCancel} variant="outline" className="rounded-full"><X size={18} className="mr-2" />Cancel</Button>
              </div>
            </div>
          </div>
        )}

        <div className="space-y-4">
          {projects.length === 0 ? (
            <div className="bg-white rounded-2xl p-12 text-center border">
              <p className="text-slate-600 mb-4">No projects yet. Add your first project!</p>
              <Button onClick={handleAdd} className="bg-[#10B981] hover:bg-[#059669] rounded-full"><Plus size={18} className="mr-2" />Add Project</Button>
            </div>
          ) : (
            projects.map((project, index) => (
              <div key={project.id} className="bg-white rounded-2xl p-6 border hover:border-[#10B981] transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-heading text-xl font-bold mb-2">{project.title}</h3>
                    <p className="text-slate-600 mb-2">{project.description}</p>
                    <div className="flex items-center gap-4 text-sm text-slate-500">
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{project.category}</span>
                      <span className="bg-slate-100 px-3 py-1 rounded-full">{project.results.length} results</span>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button onClick={() => handleEdit(project)} variant="outline" size="icon" className="rounded-full"><Edit size={18} /></Button>
                    <Button onClick={() => handleDelete(project.id)} variant="outline" size="icon" className="rounded-full text-red-600 hover:text-red-700"><Trash2 size={18} /></Button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
